import { useQueries } from "@tanstack/react-query";
import { ArrowDownUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CommentsSkeleton from "src/components/NewComponents/Skeletons/Comments";
import TipTapEditor from "src/components/TipTapEditor/TipTapEditor";
import { getService } from "src/helpers/isPes";
import { useFontSize } from "src/hooks/useFontSize";
import { useAddNote, useGetNotes } from "src/hooks/useNotes";
import EditIcon from "src/images/Icons/newIcons/exam/more/edit.svg";
import newApi from "src/newApi";

import DefaultOptionDetail from "./DefaultOptionDetail";
import { FolderNoteCard } from "./FolderNoteCard";

interface Folder {
	id: number | string;
	name?: string;
	details?: { color?: string | null } | null;
}

interface Question {
	id: number;
}

interface Settings {
	notes: boolean;
}

interface FolderInfo {
	id: string;
	name: string;
	details?: { color?: string | null } | null;
}

interface NoteItem {
	data?: { id: string | number; name?: string; note?: string };
	folderId: string;
	folder: FolderInfo;
}

interface NotesProps {
	settings: Settings;
	question: Question;
	folders?: Folder[];
	onClose: (key: string) => void;
	dispatch: (action: unknown) => void;
	actions: {
		toggleSettings: (key: string) => unknown;
	};
}

export function Notes({
	settings,
	question,
	folders = [],
	dispatch,
	actions,
}: NotesProps) {
	const { fontSizeClass } = useFontSize();
	const { t } = useTranslation();
	const [editorError, setEditorError] = useState("");
	const folderIds = folders.map((f) => String(f.id));

	const { data: noteData } = useGetNotes(question.id, settings.notes);
	const { mutateAsync: addNote } = useAddNote();

	const folderQueries = useQueries({
		queries: folderIds.map((folderId) => ({
			queryKey: ["folder-notes", getService(), folderId],

			queryFn: () =>
				newApi.getFolderItemsOffset(folderId, {
					type: "note",
					take: 50,
				}),

			enabled: !!folderId && !!settings.notes,
		})),
	});

	const allNotes: NoteItem[] = folderQueries.flatMap((query, idx) => {
		const items = (query.data as { data?: unknown[] })?.data || [];
		return items.map((item) => ({
			...(item as object),
			folderId: folderIds[idx],
			folder: { ...folders[idx], id: folderIds[idx] } as FolderInfo,
		})) as NoteItem[];
	});

	const handleAddNote = async (html: string) => {
		const isEmpty = !html || html.replace(/<[^>]*>/g, "").trim() === "";

		try {
			await addNote({ note: isEmpty ? "" : html, id: question.id });
			toast.success(t("note.saveSuccess", "Notatka została zapisana"), {
				position: toast.POSITION.BOTTOM_LEFT,
				autoClose: 3000,
			});
		} catch {
			toast.error(t("note.saveError", "Nie udało się zapisać notatki"), {
				position: toast.POSITION.BOTTOM_LEFT,
				autoClose: 5000,
			});
		}
	};

	const renderContent = () => (
		<div className="flex flex-col gap-32">
			<div className="flex flex-col gap-16">
				<TipTapEditor
					onSubmit={handleAddNote}
					initialHtml={noteData?.notatka || ""}
					placeholder={t(
						"exam.notes.placeholder",
						"Dodaj notatkę lub zdjęcie...",
					)}
					submitText={t("exam.notes.addNote", "Dodaj notatkę")}
					cancelText={undefined}
					onCancel={undefined}
					className={undefined}
					error={!!editorError}
					helperText={editorError}
					onUpdate={() => {
						if (editorError) setEditorError("");
					}}
				/>
			</div>
			{allNotes.length > 0 && (
				<div className="flex flex-col gap-20">
					<div className="flex items-center justify-between">
						<span className={fontSizeClass}>
							{t("exam.notes.myNotesInFolder", "Moje notatki w folderze")}
						</span>
						<ArrowDownUp className="icon-small" />
					</div>
					<div className="flex flex-col gap-16">
						{allNotes.map((note, idx) => (
							<FolderNoteCard
								key={idx}
								note={note.data ?? null}
								folder={note.folder}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);

	if (!settings.notes) {
		return null;
	}

	return (
		<DefaultOptionDetail
			dispatch={dispatch}
			actions={actions}
			isLoading={false}
			data={{}}
			renderContent={renderContent}
			isOpen={settings.notes}
			optionKey="notes"
			renderHeader={() => (
				<div className="flex-center-row gap-8">
					<EditIcon className="icon-normal" />
					<span className={fontSizeClass}>{t("exam.notes.title")}</span>
				</div>
			)}
			Skeleton={<CommentsSkeleton />}
		/>
	);
}
